import { AircraftLimitApiResponse, CoverageApiResponse, SubCoverageApiResponse } from '@features/insured/models/coverages/coverage-api.model'
import { AlbertValueObject } from '@features/insured/models/member/member.model'
import { FormatCurrencyAmountPipe } from '@shared/pipes/format-currency-amount.pipe'
import { hasActualValue } from '@core/utils/form-utils'

/** Shown when the insured has the coverage, but the limit is not filled. */
export const LIMIT_NOT_FILLED = '-'
/** Shown when the insured does not have the coverage at all. */
export const LIMIT_NOT_AVAILABLE = 'N/A'
export const AVN52E_VARIOUS = 'various'
export const AVN52E_CONSISTENT = 'consistent'

export type FurtherInsuredLimitKey =
  'csl' | 'hullWarAggregate' | 'confiscation' | 'avn52e' | 'sparesLocation' | 'sparesTransport' | 'avn59'

export type FurtherInsuredLimitValues = Record<FurtherInsuredLimitKey, string>

export interface FurtherInsuredLimitRow {
  key: FurtherInsuredLimitKey
  label: string
  /** The field on the insured page the value is taken from, as the short labels are easily mistaken for others. */
  source: string
}

interface LimitSource {
  subCoverageType: string
  limitType: string
}

/** The rows of the limits table, in display order. */
export const FURTHER_INSURED_LIMIT_ROWS: readonly FurtherInsuredLimitRow[] = [
  { key: 'csl', label: 'CSL', source: 'Aviation Combined Third Party & Passenger Liability Limit' },
  { key: 'hullWarAggregate', label: 'Hull War Aggregate', source: 'Hull War and Allied Perils Aggregate Limit' },
  { key: 'confiscation', label: 'Confiscation Limit', source: 'Hull War and Allied Perils Confiscation Sub-Limit' },
  { key: 'avn52e', label: 'AVN52E', source: 'Aviation Third Party War Excess Limits per aircraft type' },
  { key: 'sparesLocation', label: 'Spares Location', source: 'Spares Location (Excess Only) Limit' },
  { key: 'sparesTransport', label: 'Spares Transport', source: 'Spares Transport (Excess Only) Limit' },
  { key: 'avn59', label: 'AVN59', source: 'Non-Aviation Excess Liability Limit' }
]

// The insured coverage each row is taken from (AVN52E only needs its sub-coverage, its limits are per aircraft).
const LIMIT_SOURCES: Readonly<Record<Exclude<FurtherInsuredLimitKey, 'avn52e'>, LimitSource>> = {
  csl: { subCoverageType: 'AVIATION_COMBINED_THIRD_PARTY_AND_PASSENGER_LIABILITY', limitType: 'LIMIT' },
  hullWarAggregate: { subCoverageType: 'HULL_WAR_AND_ALLIED_PERILS', limitType: 'AGGREGATE' },
  confiscation: { subCoverageType: 'HULL_WAR_AND_ALLIED_PERILS', limitType: 'CONFISCATION' },
  sparesLocation: { subCoverageType: 'SPARES_LOCATION_EXCESS_ONLY', limitType: 'LIMIT' },
  sparesTransport: { subCoverageType: 'SPARES_TRANSPORT_EXCESS_ONLY', limitType: 'LIMIT' },
  avn59: { subCoverageType: 'NON_AVIATION_EXCESS_LIABILITY', limitType: 'LIMIT' }
}
const AVN52E_SUB_COVERAGE_TYPE = 'AVIATION_THIRD_PARTY_WAR_EXCESS_LIABILITY'

const currencyAmount = new FormatCurrencyAmountPipe()

/** A value overridden in Albert wins over the one synchronised from Origami. */
function effectiveValue<T>(value: AlbertValueObject<T | null> | null | undefined): T | null {
  if (!value) {
    return null
  }
  return value.changed ? value.localValue : value.externalValue ?? value.localValue
}

function formatLimit(subCoverage: SubCoverageApiResponse | undefined, limitType: string): string {
  if (subCoverage === undefined) {
    return LIMIT_NOT_AVAILABLE
  }
  const limit = (subCoverage.subcoverageLimits ?? []).find(l => l.subcoverageLimitType === limitType)
  const amount = limit ? effectiveValue(limit.subcoverageLimitValue) : null
  if (!hasActualValue(amount)) {
    return LIMIT_NOT_FILLED
  }
  const currency = limit ? effectiveValue(limit.subcoverageLimitCurrency) : null
  return currencyAmount.transform(amount, currency ?? '').trim()
}

/**
 * "consistent" when all aircraft of the insured share one AVN52E limit, "various" when they differ, "-" when none has
 * a war excess limit, neither entered in Albert nor in the fleet list.
 */
function avn52eLimitKind(subCoverage: SubCoverageApiResponse): string {
  if (subCoverage.aircraftLimitConsistency === 'CONSISTENT') {
    return AVN52E_CONSISTENT
  }
  return subCoverage.aircraftLimitConsistency === 'VARIOUS' ? AVN52E_VARIOUS : LIMIT_NOT_FILLED
}

/**
 * Builds the limits of one further insured from the coverages of its policy; AVN52E comes from the consistency of
 * the war excess limits per aircraft type, which the backend derives.
 */
export function buildFurtherInsuredLimits(coverages: CoverageApiResponse[]): FurtherInsuredLimitValues {
  const subCoverages = coverages.flatMap(coverage => coverage.subcoverages ?? [])
  const findSubCoverage = (type: string): SubCoverageApiResponse | undefined =>
    subCoverages.find(sub => sub.subCoverageType === type)

  const values = Object.fromEntries(
    Object.entries(LIMIT_SOURCES).map(([key, source]) => [key, formatLimit(findSubCoverage(source.subCoverageType), source.limitType)])
  ) as Omit<FurtherInsuredLimitValues, 'avn52e'>

  const warExcess = findSubCoverage(AVN52E_SUB_COVERAGE_TYPE)
  return {
    ...values,
    avn52e: warExcess === undefined ? LIMIT_NOT_AVAILABLE : avn52eLimitKind(warExcess)
  }
}

/**
 * The AVN52E limits per aircraft type the insured shows: the ones entered in Albert, or Origami's, derived from the
 * fleet list, as long as none are entered.
 */
export function avn52eAircraftLimits(coverages: CoverageApiResponse[]): AircraftLimitApiResponse[] {
  const warExcess = coverages
    .flatMap(coverage => coverage.subcoverages ?? [])
    .find(sub => sub.subCoverageType === AVN52E_SUB_COVERAGE_TYPE)
  const entered = warExcess?.aircraftLimits ?? []
  return entered.length > 0 ? entered : warExcess?.origamiAircraftLimits ?? []
}

/** Values for an insured without any policy in the certificate's period. */
export function unavailableFurtherInsuredLimits(): FurtherInsuredLimitValues {
  return buildFurtherInsuredLimits([])
}
